import { useState, useEffect, useCallback, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Menu, Search, Moon, Sun, Shuffle, X } from "lucide-react";
import { useTheme } from "@/context/ThemeContext";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

function TextSizeControl({ testPrefix = "" }) {
  const { fontSize, setFontSize } = useTheme();
  const opts = [
    { key: "sm", label: "A", cls: "text-xs" },
    { key: "md", label: "A", cls: "text-sm" },
    { key: "lg", label: "A", cls: "text-base" },
  ];
  return (
    <div className="flex items-center overflow-hidden rounded border" title="Text size" data-testid={`${testPrefix}text-size`}>
      {opts.map((o) => (
        <button
          key={o.key}
          onClick={() => setFontSize(o.key)}
          data-testid={`${testPrefix}text-size-${o.key}`}
          aria-label={`Text size ${o.key}`}
          className={`flex h-9 w-8 items-center justify-center font-serif-arc font-bold leading-none transition-colors ${o.cls} ${fontSize === o.key ? "" : "hover:bg-black/5 dark:hover:bg-white/10"}`}
          style={fontSize === o.key ? { backgroundColor: "var(--ghoulax)", color: "var(--ghoulax-fg)" } : {}}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

const NAV = [
  { label: "Home", to: "/" },
  { label: "Ghoulax", to: "/ghoulax" },
  { label: "Zyfley", to: "/zyfley" },
  { label: "Quizzes", to: "/quizzes" },
];

export default function Navbar() {
  const { theme, toggle } = useTheme();
  const { user, isAdmin, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchOpen, setSearchOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const debRef = useRef();

  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  const runSearch = useCallback((value) => {
    clearTimeout(debRef.current);
    debRef.current = setTimeout(async () => {
      if (!value.trim()) { setResults([]); return; }
      try {
        const { data } = await api.get(`/search`, { params: { q: value } });
        setResults(data);
      } catch (e) { setResults([]); }
    }, 200);
  }, []);

  const randomArticle = async () => {
    try {
      const { data } = await api.get("/random");
      if (Math.random() < 0.25) toast("You have discovered forbidden knowledge.");
      navigate(`/article/${data.slug}`);
    } catch (e) { toast.error("No articles found."); }
  };

  const openResult = (slug) => {
    setSearchOpen(false); setQ(""); setResults([]);
    navigate(`/article/${slug}`);
  };

  return (
    <header className="sticky top-0 z-40 border-b" style={{ backgroundColor: "var(--arc-bg)" }}>
      <nav className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-4 py-3 sm:px-6">
        <Link to="/" data-testid="nav-logo" className="font-serif-arc text-lg font-black tracking-tight sm:text-xl">
          THE ARCHIVES
        </Link>

        <div className="hidden items-center gap-1 md:flex">
          {NAV.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              data-testid={`nav-link-${n.label.toLowerCase()}`}
              className="rounded px-3 py-2 text-sm font-medium hover:bg-black/5 dark:hover:bg-white/10 transition-colors"
            >
              {n.label}
            </Link>
          ))}
          <button
            data-testid="nav-search-button"
            onClick={() => setSearchOpen(true)}
            className="flex items-center gap-2 rounded px-3 py-2 text-sm font-medium hover:bg-black/5 dark:hover:bg-white/10 transition-colors"
          >
            <Search size={16} /> Search
          </button>
          <button
            data-testid="nav-random-button"
            onClick={randomArticle}
            className="flex items-center gap-2 rounded px-3 py-2 text-sm font-medium hover:bg-black/5 dark:hover:bg-white/10 transition-colors"
          >
            <Shuffle size={16} /> Random Article
          </button>
          {isAdmin && (
            <Link to="/admin" data-testid="nav-admin-link" className="rounded px-3 py-2 text-sm font-semibold ghoulax-accent hover:underline">Admin</Link>
          )}
          {user && user.role === "editor" && (
            <Link to="/editor" data-testid="nav-editor-link" className="rounded px-3 py-2 text-sm font-semibold zyfley-accent hover:underline">Editor</Link>
          )}
          {user ? (
            <button data-testid="nav-logout" onClick={logout} className="rounded px-3 py-2 text-sm font-medium hover:bg-black/5 dark:hover:bg-white/10">Logout</button>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login" className="rounded px-3 py-2 text-sm font-medium hover:bg-black/5 dark:hover:bg-white/10">Login</Link>
              <Link to="/register" data-testid="nav-register" className="rounded px-3 py-2 text-sm font-semibold zyfley-accent hover:underline">Sign Up</Link>
            </>
          )}
          <button
            data-testid="nav-theme-toggle"
            onClick={toggle}
            aria-label="Toggle theme"
            className="ml-1 flex h-9 w-9 items-center justify-center rounded border hover:bg-black/5 dark:hover:bg-white/10 transition-colors"
          >
            {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
          </button>
          <div className="ml-1"><TextSizeControl /></div>
        </div>

        {/* Mobile */}
        <div className="flex items-center gap-2 md:hidden">
          <button data-testid="nav-search-button-mobile" onClick={() => setSearchOpen(true)} className="flex h-9 w-9 items-center justify-center rounded border">
            <Search size={16} />
          </button>
          <button data-testid="nav-theme-toggle-mobile" onClick={toggle} className="flex h-9 w-9 items-center justify-center rounded border">
            {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
          </button>
          <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
            <SheetTrigger asChild>
              <button data-testid="nav-hamburger" className="flex h-9 w-9 items-center justify-center rounded border">
                <Menu size={18} />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-72" style={{ backgroundColor: "var(--arc-bg)" }}>
              <div className="mt-6 flex flex-col gap-1">
                <MobileLink to="/" label="Home" />
                <MobileLink to="/ghoulax" label="Ghoulax Archives" />
                <MobileLink to="/zyfley" label="Zyfley Encyclopedia" />
                <button data-testid="mobile-search" onClick={() => { setMenuOpen(false); setSearchOpen(true); }} className="rounded px-3 py-2 text-left text-sm font-medium hover:bg-black/5 dark:hover:bg-white/10">Search</button>
                <button data-testid="mobile-random" onClick={() => { setMenuOpen(false); randomArticle(); }} className="rounded px-3 py-2 text-left text-sm font-medium hover:bg-black/5 dark:hover:bg-white/10">Random Article</button>
                <MobileLink to="/about" label="About" />
                <MobileLink to="/disclaimer" label="Disclaimer" />
                <MobileLink to="/achievements" label="Achievements" />
                <MobileLink to="/quizzes" label="Quizzes" />
                <div className="my-2 border-t" />
                <div className="flex items-center justify-between px-3 py-2">
                  <span className="text-sm font-medium">Text size</span>
                  <TextSizeControl testPrefix="mobile-" />
                </div>
                <div className="my-2 border-t" />
                {isAdmin && <MobileLink to="/admin" label="Admin" />}
                {user && user.role === "editor" && <MobileLink to="/editor" label="Editor Workspace" />}
                {user ? (
                  <button onClick={() => { setMenuOpen(false); logout(); }} className="rounded px-3 py-2 text-left text-sm font-medium hover:bg-black/5 dark:hover:bg-white/10">Logout</button>
                ) : (
                  <>
                    <MobileLink to="/login" label="Login" />
                    <MobileLink to="/register" label="Sign Up as Editor" />
                  </>
                )}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </nav>

      <Dialog open={searchOpen} onOpenChange={setSearchOpen}>
        <DialogContent className="max-w-xl gap-0 p-0" style={{ backgroundColor: "var(--arc-card)" }}>
          <div className="flex items-center gap-2 border-b px-4 py-3">
            <Search size={18} className="opacity-60" />
            <Input
              data-testid="search-input"
              autoFocus
              value={q}
              onChange={(e) => { setQ(e.target.value); runSearch(e.target.value); }}
              placeholder="Search the archives…"
              className="border-0 shadow-none focus-visible:ring-0"
            />
          </div>
          <div className="max-h-[60vh] overflow-y-auto p-2">
            {q && results.length === 0 && <p className="px-3 py-6 text-center text-sm" style={{ color: "var(--arc-text-2)" }}>No results found.</p>}
            {results.map((r) => (
              <button
                key={r.slug}
                data-testid={`search-result-${r.slug}`}
                onClick={() => openResult(r.slug)}
                className="block w-full rounded px-3 py-3 text-left hover:bg-black/5 dark:hover:bg-white/10 transition-colors"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-serif-arc font-bold">{r.title}</span>
                  <span className={`text-xs font-semibold ${r.encyclopedia === "ghoulax" ? "ghoulax-accent" : "zyfley-accent"}`}>
                    {r.encyclopedia === "ghoulax" ? "Ghoulax Archives" : "Zyfley Encyclopedia"}
                  </span>
                </div>
                <p className="mt-0.5 line-clamp-1 text-sm" style={{ color: "var(--arc-text-2)" }}>{r.description}</p>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </header>
  );
}

function MobileLink({ to, label }) {
  return (
    <Link to={to} data-testid={`mobile-link-${label.toLowerCase().replace(/\s+/g, "-")}`} className="rounded px-3 py-2 text-sm font-medium hover:bg-black/5 dark:hover:bg-white/10">
      {label}
    </Link>
  );
}
